import React, { useState } from 'react';

/**
 * SMSTermsPage.jsx
 * 
 * Twilio Toll-Free Verification Compliant SMS Consent Page
 * 
 * CRITICAL FOR VERIFICATION:
 * - Checkbox consent language EXPLICITLY lists message types
 * - Language specifies "informational/transactional" (not promotional)
 * - Consent matches declared use case: tee time reminders & booking notifications
 * - All CTIA/TCPA required disclosures included
 * - Checkbox visible immediately (not behind lookup)
 * 
 * Route: /sms-terms
 * 
 * Created: December 2025
 * Updated: January 2026 - Twilio verification compliant language
 */

const SMSTermsPage = () => {
  const [phone, setPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Format phone number as user types
  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
    setMessage({ type: '', text: '' });
  };

  // Save SMS preference (opt-in or opt-out)
  const savePreference = async () => {
    const cleanPhone = phone.replace(/\D/g, '');
    
    if (cleanPhone.length !== 10) {
      setMessage({ type: 'error', text: 'Please enter a valid 10-digit phone number.' });
      return;
    }

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const res = await fetch(`/api/sms-preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          phone: cleanPhone, 
          sms_enabled: smsConsent 
        })
      });

      const data = await res.json();

      if (res.ok) {
        setMessage({ 
          type: 'success', 
          text: '✓ Success! You are now opted in to receive SMS notifications for your tee times.'
        });
      } else if (res.status === 404) {
        setMessage({ 
          type: 'error', 
          text: 'Phone number not found in our system. Please contact your golf group organizer to be added, or verify you entered the correct number.'
        });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to update preferences.' });
      }
    } catch (err) {
      console.error('Save error:', err);
      setMessage({ type: 'error', text: 'Unable to save preferences. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.logoContainer}>
          <div style={styles.logoIcon}>⛳</div>
          <span style={styles.logoText}>Tee Time Organizer</span>
        </div>
        <a href="/" style={styles.homeLink}>← Back to Home</a>
      </header>

      {/* Main Content */}
      <main style={styles.main}>
        <div style={styles.card}>
          {/* Page Title */}
          <div style={styles.titleSection}>
            <div style={styles.iconCircle}>
              <span style={styles.smsIcon}>💬</span>
            </div>
            <h1 style={styles.title}>SMS Text Message Consent</h1>
            <p style={styles.subtitle}>Tee Time Reminders & Booking Notifications</p>
          </div>

          {/* ========== PROGRAM DESCRIPTION - REQUIRED BY TWILIO ========== */}
          <section style={styles.programSection}>
            <h2 style={styles.programTitle}>📋 SMS Program Description</h2>
            <div style={styles.programBox}>
              <p style={styles.programText}>
                <strong>Tee Time Organizer SMS Program</strong> sends informational text messages 
                to help golfers manage their tee time reservations. This is NOT a marketing or 
                promotional messaging program.
              </p>
              <p style={styles.programText}>
                <strong>Message Purpose:</strong> Transactional and informational messages only, 
                including tee time booking confirmations, day-before reminders, same-day reminders 
                with weather updates, waitlist notifications, and schedule change alerts.
              </p>
              <p style={{...styles.programText, marginBottom: 0}}>
                <strong>Who Can Opt-In:</strong> Only registered members of golf groups using 
                Tee Time Organizer can opt-in to receive SMS messages.
              </p>
            </div>
          </section>

          {/* ========== OPT-IN SECTION - TWILIO COMPLIANT ========== */}
          <section style={styles.optInSection}>
            <h2 style={styles.optInTitle}>📱 Opt-In to SMS Notifications</h2>
            
            {/* Phone Input */}
            <div style={styles.phoneInputGroup}>
              <label style={styles.inputLabel}>Your Mobile Phone Number (US only)</label>
              <input
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                placeholder="(555) 123-4567"
                style={styles.phoneInput}
                maxLength={14}
                aria-label="Mobile phone number"
              />
            </div>

            {/* ========== CONSENT CHECKBOX - EXPLICIT LANGUAGE ========== */}
            <div style={{
              ...styles.checkboxContainer,
              backgroundColor: smsConsent ? '#f0fdf4' : '#ffffff',
              borderColor: smsConsent ? '#22c55e' : '#d1d5db'
            }}>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={smsConsent}
                  onChange={(e) => setSmsConsent(e.target.checked)}
                  style={styles.checkbox}
                  aria-describedby="consent-details"
                />
                <div style={styles.checkboxContent} id="consent-details">
                  <div style={styles.checkboxTitle}>
                    Yes, I agree to receive SMS text messages
                  </div>
                  
                  {/* EXPLICIT CONSENT LANGUAGE - MATCHES USE CASE */}
                  <div style={styles.consentTextBox}>
                    <p style={styles.consentParagraph}>
                      By checking this box, I expressly consent to receive the following 
                      <strong> informational and transactional </strong> 
                      SMS text messages from Tee Time Organizer at the mobile phone number provided:
                    </p>
                    <ul style={styles.consentList}>
                      <li><strong>Tee time booking confirmations</strong> when I RSVP for a round</li>
                      <li><strong>24-hour reminders</strong> the day before my scheduled tee time</li>
                      <li><strong>Same-day reminders</strong> on the morning of my tee time (includes weather)</li>
                      <li><strong>Waitlist alerts</strong> when a spot becomes available</li>
                      <li><strong>Cancellation notices</strong> if a tee time is cancelled or rescheduled</li>
                    </ul>
                    <p style={styles.consentParagraph}>
                      I understand that message frequency varies based on my tee time activity 
                      (typically 2-6 messages per week during golf season). 
                      <strong> Message and data rates may apply.</strong>
                    </p>
                  </div>

                  {/* REQUIRED DISCLOSURES */}
                  <div style={styles.disclosureBox}>
                    <p style={styles.disclosureText}>
                      📱 <strong>Text STOP to +1 (844) 766-1040 to opt out at any time.</strong>
                    </p>
                    <p style={styles.disclosureText}>
                      ❓ <strong>Text HELP for assistance</strong> or contact support@teetimeorganizer.net
                    </p>
                    <p style={{...styles.disclosureText, marginBottom: 0}}>
                      ⚠️ <strong>Consent is not required</strong> to use Tee Time Organizer services. 
                      Email notifications are always available as an alternative.
                    </p>
                  </div>
                </div>
              </label>
            </div>

            {/* Status/Error Messages */}
            {message.text && (
              <div style={{
                ...styles.messageBox,
                backgroundColor: message.type === 'error' ? '#fef2f2' : '#f0fdf4',
                borderColor: message.type === 'error' ? '#fecaca' : '#bbf7d0',
                color: message.type === 'error' ? '#dc2626' : '#16a34a'
              }}>
                {message.text}
              </div>
            )}

            {/* Save Button - Only enabled when consent checkbox is checked */}
            <button
              onClick={savePreference}
              disabled={loading || phone.replace(/\D/g, '').length !== 10 || !smsConsent}
              style={{
                ...styles.saveButton,
                opacity: (loading || phone.replace(/\D/g, '').length !== 10 || !smsConsent) ? 0.5 : 1,
                cursor: (loading || phone.replace(/\D/g, '').length !== 10 || !smsConsent) ? 'not-allowed' : 'pointer',
                backgroundColor: '#2d6a4f'
              }}
            >
              {loading ? 'Saving...' : 'Submit My Consent for SMS Notifications'}
            </button>

            <p style={styles.helperText}>
              Enter your phone number and check the consent box to enable the submit button.
            </p>
            <p style={{...styles.helperText, marginTop: '8px', fontSize: '13px', color: '#9ca3af'}}>
              Already receiving SMS and want to opt out? Simply reply STOP to any message.
            </p>
          </section>
          {/* ========== END OPT-IN SECTION ========== */}

          {/* Divider */}
          <div style={styles.divider}></div>

          {/* Additional Terms */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Message & Data Rates</h2>
            <p style={styles.paragraph}>
              Standard message and data rates from your wireless carrier may apply to SMS messages 
              sent and received. Tee Time Organizer does not charge for SMS notifications, but your 
              carrier may charge based on your mobile plan.
            </p>
            <p style={styles.paragraph}>
              If you have questions about your text or data plan, contact your wireless provider.
            </p>
          </section>

          {/* How to Opt Out */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>How to Opt-Out</h2>
            <p style={styles.paragraph}>
              You can stop receiving SMS messages at any time using any of these methods:
            </p>
            <div style={styles.optOutMethods}>
              <div style={styles.optOutMethod}>
                <span style={styles.optOutIcon}>📱</span>
                <div>
                  <strong>Reply STOP</strong>
                  <p style={styles.optOutDesc}>Text STOP to any message you receive from us</p>
                </div>
              </div>
              <div style={styles.optOutMethod}>
                <span style={styles.optOutIcon}>🌐</span>
                <div>
                  <strong>Use This Page</strong>
                  <p style={styles.optOutDesc}>Uncheck the consent box above and click save</p>
                </div>
              </div>
              <div style={styles.optOutMethod}>
                <span style={styles.optOutIcon}>📧</span>
                <div>
                  <strong>Contact Support</strong>
                  <p style={styles.optOutDesc}>Email support@teetimeorganizer.net</p>
                </div>
              </div>
            </div>
          </section>

          {/* Privacy */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Privacy & Data Use</h2>
            <p style={styles.paragraph}>
              Your phone number is used solely for sending tee time-related notifications. 
              We do not sell, rent, or share your phone number with third parties for marketing purposes.
            </p>
            <p style={styles.paragraph}>
              For complete details, please review our{' '}
              <a href="/privacy" style={styles.link}>Privacy Policy</a>.
            </p>
          </section>

          {/* Contact Information */}
          <section style={styles.section}>
            <h2 style={styles.sectionTitle}>Contact Us</h2>
            <div style={styles.contactBox}>
              <p style={styles.contactLine}><strong>Tee Time Organizer</strong></p>
              <p style={styles.contactLine}>Email: <a href="mailto:support@teetimeorganizer.net" style={styles.link}>support@teetimeorganizer.net</a></p>
              <p style={styles.contactLine}>SMS Support: Text HELP to +1 (844) 766-1040</p>
              <p style={styles.contactLine}>Website: <a href="https://teetimeorganizer.net" style={styles.link}>teetimeorganizer.net</a></p>
            </div>
          </section>

          {/* Footer Note */}
          <div style={styles.footerNote}>
            <p style={styles.lastUpdated}>Last Updated: January 2026</p>
            <p style={styles.version}>SMS Terms Version 2.0</p>
          </div>
        </div>
      </main>

      {/* Page Footer */}
      <footer style={styles.footer}>
        <p>© 2026 Tee Time Organizer. All rights reserved.</p>
        <div style={styles.footerLinks}>
          <a href="/privacy" style={styles.footerLink}>Privacy Policy</a>
          <span style={styles.footerDivider}>|</span>
          <a href="/terms" style={styles.footerLink}>Terms of Service</a>
          <span style={styles.footerDivider}>|</span>
          <a href="mailto:support@teetimeorganizer.net" style={styles.footerLink}>Contact Support</a>
        </div>
      </footer>
    </div>
  );
};

// Styles
const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f5f7f5',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e0e7e0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  logoIcon: {
    fontSize: '28px',
  },
  logoText: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#2d6a4f',
  },
  homeLink: {
    color: '#2d6a4f',
    textDecoration: 'none',
    fontWeight: '500',
    fontSize: '15px',
  },
  main: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '40px 20px',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    padding: '48px',
  },
  titleSection: {
    textAlign: 'center',
    marginBottom: '40px',
  },
  iconCircle: {
    width: '80px',
    height: '80px',
    backgroundColor: '#e8f5e9',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    border: '3px solid #2d6a4f',
  },
  smsIcon: {
    fontSize: '36px',
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#1a1a1a',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '18px',
    color: '#666666',
    margin: '0',
  },
  // Program Description Section
  programSection: {
    marginBottom: '32px',
  },
  programTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1f2937',
    marginTop: '0',
    marginBottom: '16px',
  },
  programBox: {
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '24px',
  },
  programText: {
    fontSize: '15px',
    lineHeight: '1.7',
    color: '#374151',
    margin: '0 0 16px 0',
  },
  // Opt-In Section Styles
  optInSection: {
    backgroundColor: '#eff6ff',
    border: '2px solid #3b82f6',
    borderRadius: '16px',
    padding: '32px',
    marginBottom: '40px',
  },
  optInTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e40af',
    marginTop: '0',
    marginBottom: '20px',
  },
  phoneInputGroup: {
    marginBottom: '24px',
  },
  inputLabel: {
    display: 'block',
    fontSize: '15px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px',
  },
  phoneInput: {
    width: '100%',
    padding: '14px 16px',
    fontSize: '18px',
    border: '2px solid #d1d5db',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  // Checkbox Container
  checkboxContainer: {
    padding: '24px',
    borderRadius: '12px',
    border: '2px solid',
    marginBottom: '24px',
    transition: 'all 0.2s',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    cursor: 'pointer',
  },
  checkbox: {
    width: '28px',
    height: '28px',
    marginTop: '2px',
    accentColor: '#22c55e',
    flexShrink: 0,
    cursor: 'pointer',
  },
  checkboxContent: {
    flex: 1,
  },
  checkboxTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#1f2937',
    marginBottom: '16px',
  },
  // Consent Text Box - THE CRITICAL PART
  consentTextBox: {
    backgroundColor: '#ffffff',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '16px',
  },
  consentParagraph: {
    fontSize: '14px',
    lineHeight: '1.7',
    color: '#374151',
    margin: '0 0 12px 0',
  },
  consentList: {
    margin: '12px 0 16px 0',
    paddingLeft: '20px',
    fontSize: '14px',
    lineHeight: '2',
    color: '#374151',
  },
  // Disclosure Box
  disclosureBox: {
    backgroundColor: '#fef3c7',
    border: '1px solid #fcd34d',
    borderRadius: '8px',
    padding: '16px',
  },
  disclosureText: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#92400e',
    margin: '0 0 8px 0',
  },
  // Message Box
  messageBox: {
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid',
    marginBottom: '20px',
    fontSize: '15px',
    fontWeight: '500',
  },
  // Save Button
  saveButton: {
    width: '100%',
    padding: '18px',
    fontSize: '18px',
    fontWeight: '700',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  helperText: {
    textAlign: 'center',
    fontSize: '14px',
    color: '#6b7280',
    marginTop: '16px',
    marginBottom: '0',
  },
  divider: {
    height: '2px',
    backgroundColor: '#e5e7eb',
    margin: '40px 0',
  },
  section: {
    marginBottom: '32px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#2d6a4f',
    marginBottom: '16px',
    marginTop: '0',
  },
  paragraph: {
    fontSize: '16px',
    lineHeight: '1.7',
    color: '#333333',
    marginBottom: '12px',
  },
  // Opt Out Methods
  optOutMethods: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  optOutMethod: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '16px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  optOutIcon: {
    fontSize: '24px',
  },
  optOutDesc: {
    fontSize: '14px',
    color: '#6b7280',
    margin: '4px 0 0 0',
  },
  // Contact Box
  contactBox: {
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '20px',
  },
  contactLine: {
    fontSize: '15px',
    color: '#374151',
    margin: '0 0 8px 0',
  },
  link: {
    color: '#2d6a4f',
    textDecoration: 'none',
    fontWeight: '500',
  },
  footerNote: {
    textAlign: 'center',
    marginTop: '40px',
    paddingTop: '24px',
    borderTop: '1px solid #e5e7eb',
  },
  lastUpdated: {
    fontSize: '14px',
    color: '#888888',
    marginBottom: '4px',
  },
  version: {
    fontSize: '14px',
    color: '#666666',
    fontWeight: '500',
  },
  footer: {
    textAlign: 'center',
    padding: '24px',
    color: '#666666',
    fontSize: '14px',
    borderTop: '1px solid #e0e7e0',
    backgroundColor: '#ffffff',
  },
  footerLinks: {
    marginTop: '8px',
  },
  footerLink: {
    color: '#2d6a4f',
    textDecoration: 'none',
  },
  footerDivider: {
    margin: '0 12px',
    color: '#cccccc',
  },
};

export default SMSTermsPage;
